/**
 * Offline cache coherence for multi-instance CDU (#996 Phase 2).
 * Keeps request.liquidCoolingCduConfigurations aligned with the CDU API / React Query.
 * Mirrors smallUpsOfflineSync.
 */

import {
  offlineCache,
  type OfflineLiquidCoolingCduOperation,
} from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupLiquidCoolingCdusByStatus } from '../components/liquidcoolingcdus/liquidCoolingCduListUtils';

import type {
  GroupedLiquidCoolingCduConfigurations,
  LiquidCoolingCduConfiguration,
} from '../types/liquidCoolingCdu';

/** CDU list from cached request detail (offline reads). */
export async function getCachedLiquidCoolingCduList(
  requestId: string,
): Promise<LiquidCoolingCduConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.liquidCoolingCduConfigurations ?? [];
}

/** Grouped units derived from cached list (same shape as GET …/liquid-cooling-cdus/grouped). */
export async function getCachedLiquidCoolingCduGrouped(
  requestId: string,
): Promise<GroupedLiquidCoolingCduConfigurations> {
  return groupLiquidCoolingCdusByStatus(
    await getCachedLiquidCoolingCduList(requestId),
  );
}

/** Single unit from cached list when offline. */
export async function getCachedLiquidCoolingCduById(
  requestId: string,
  liquidCoolingCduId: number,
): Promise<LiquidCoolingCduConfiguration | undefined> {
  return (await getCachedLiquidCoolingCduList(requestId)).find(
    unit => unit.id === liquidCoolingCduId,
  );
}

/**
 * After a successful online CDU fetch or mutation refetch, mirror units onto
 * the cached request so offline reads stay consistent.
 */
export async function mirrorLiquidCoolingCduToOfflineRequestCache(
  requestId: string,
  liquidCoolingCdus: LiquidCoolingCduConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestLiquidCoolingCdus(
    requestId,
    liquidCoolingCdus,
  );
}

/** Pending offline CDU queue entries for one request (create/update/delete/reorder). */
export async function getPendingLiquidCoolingCduOpsForRequest(
  requestId: string,
): Promise<
  Awaited<ReturnType<typeof offlineCache.getPendingLiquidCoolingCduOperations>>
> {
  const pending = await offlineCache.getPendingLiquidCoolingCduOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline CDU ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightLiquidCoolingCduOpsForRequest(
  requestId: string,
): Promise<OfflineLiquidCoolingCduOperation[]> {
  const queue = await offlineCache.getOfflineLiquidCoolingCduQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline CDU operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic unit already written
 * into the base list is not duplicated.
 */
export function applyPendingLiquidCoolingCduOperations(
  requestId: string,
  base: LiquidCoolingCduConfiguration[],
  ops: OfflineLiquidCoolingCduOperation[],
): LiquidCoolingCduConfiguration[] {
  return applyPendingRepeatableOperations(requestId, base, ops, {
    instanceId: op => op.liquidCoolingCduId,
    reorderIds: op => op.liquidCoolingCduIds,
  });
}

/** Cached CDU list with pending offline operations overlaid (offline reads). */
export async function getOfflineLiquidCoolingCduList(
  requestId: string,
): Promise<LiquidCoolingCduConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedLiquidCoolingCduList(requestId),
    getPendingLiquidCoolingCduOpsForRequest(requestId),
  ]);
  return applyPendingLiquidCoolingCduOperations(requestId, base, ops);
}

/** Grouped units (existing/new) from the overlaid offline list. */
export async function getOfflineLiquidCoolingCduGrouped(
  requestId: string,
): Promise<GroupedLiquidCoolingCduConfigurations> {
  return groupLiquidCoolingCdusByStatus(
    await getOfflineLiquidCoolingCduList(requestId),
  );
}

/** Single unit from the overlaid offline list (includes pending creates). */
export async function getOfflineLiquidCoolingCduById(
  requestId: string,
  liquidCoolingCduId: number,
): Promise<LiquidCoolingCduConfiguration | undefined> {
  return (await getOfflineLiquidCoolingCduList(requestId)).find(
    unit => unit.id === liquidCoolingCduId,
  );
}
